import { useAtom } from 'jotai';
import { Badge, Button, ConfirmationModal, toast } from '@/components';
import { HouseholdSection } from '@/components/HouseholdSection';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { MailIcon, UnlinkIcon } from '@/components/Icons';
import { PartnerStatusEnum } from '@/shared/enums';
import { AddHouseholdMemberModal } from './AddHouseholdMemberModal';
import { useState } from 'react';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { deleteHouseholdMember } from '@/features/profile/widgets/household/api/deleteHouseholdMember';
import { IAdultHouseholdMemberDto } from '@/shared/interfaces';
import { fetchProfile } from '@/api/profile/fetchProfile';
import { profileAtom } from '../model/store';
import * as React from 'react';
import { getHouseholdMemberStatus } from '../lib/getHouseholdMemberStatus';
import { addHouseholdMember } from '../api/addHouseholdMember';
import { Utils } from '@/shared/Utils';
import { Link } from 'react-router';
import { ProfilePaths } from '@/features/profile/model/types';
type Props = {
    canInvite: boolean;
};

export const profileQueryAtom = atomWithQuery(() => ({
    queryKey: ['profile'],
    queryFn: fetchProfile,
    enabled: false,
}));

const deleteMemberMutationAtom = atomWithMutation(() => ({
    mutationFn: deleteHouseholdMember,
}));
export function AdultHouseholdSection({ canInvite }: Props) {
    const [profile, setProfileAtom] = useAtom(profileAtom);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [memberToDelete, setMemberToDelete] =
        useState<IAdultHouseholdMemberDto | null>(null);
    const [{ refetch }] = useAtom(profileQueryAtom);
    const [{ mutate: deleteMember, isPending }] = useAtom(
        deleteMemberMutationAtom,
    );
    const handleRefetch = async () => {
        try {
            const { data: updatedProfile } = await refetch();
            setProfileAtom(updatedProfile);
        } catch (e) {
            window.location.reload();
        }
    };

    const performDelete = async () => {
        if (!memberToDelete) {
            return;
        }

        deleteMember(memberToDelete, {
            onSuccess: handleRefetch,
            onSettled: () => setMemberToDelete(null),
            onError: () => {
                toast.addToast({
                    type: 'error',
                    message: R.ErrorDeletePartner,
                });
            },
        });
    };

    const [beforeLink, afterLink] =
        R.CannotInviteAdultsInHousehold.split('{0}');

    return canInvite ? (
        <HouseholdSection
            title={R.AdultsInHousehold}
            action={
                <Button
                    variant="secondary"
                    outline
                    onClick={() => setIsAddModalOpen(true)}
                >
                    <MailIcon />
                    {R.InviteAdult}
                </Button>
            }
            hasChildren={profile.AdultHouseholdMembers.length > 0}
        >
            {profile.AdultHouseholdMembers.map((member, index) => (
                <div
                    key={member.Cpr}
                    className={
                        index !== profile.AdultHouseholdMembers.length - 1
                            ? 'border-b border-(--color-border-muted)'
                            : ''
                    }
                >
                    <div className="flex items-center justify-between px-6 py-4">
                        <div className="(--color-text-primary) text-lg font-medium">
                            {member.Name}
                        </div>

                        <div className="flex items-center gap-6">
                            <Badge
                                type={
                                    member.Status === PartnerStatusEnum.Invited
                                        ? 'attention'
                                        : 'success'
                                }
                            >
                                {getHouseholdMemberStatus(member.Status)}
                            </Badge>

                            <Button
                                variant="ghost"
                                onClick={() => setMemberToDelete(member)}
                            >
                                <UnlinkIcon />
                                {R.Unlink}
                            </Button>
                        </div>
                    </div>
                </div>
            ))}

            {isAddModalOpen && (
                <AddHouseholdMemberModal
                    onClose={(shouldRefetch) => {
                        if (shouldRefetch) {
                            handleRefetch();
                        }
                        setIsAddModalOpen(false);
                    }}
                    onSubmitMember={addHouseholdMember}
                />
            )}

            {memberToDelete && (
                <ConfirmationModal
                    title={R.RemovePartner}
                    onClose={() => setMemberToDelete(null)}
                    message={R.RemovePartnerText}
                    confirmButtonText={R.RemoveHouseholdMember}
                    confirmButtonClick={performDelete}
                    cancelButtonText={R.KeepHouseholdMember}
                    confirmIsProcessing={isPending}
                />
            )}
        </HouseholdSection>
    ) : (
        <div className="border-border-default relative rounded-2xl border bg-white">
            <div className="border-border-muted bg-bg-subtle flex items-center rounded-2xl border-b px-6 py-5">
                <div className="mr-4 flex flex-1 flex-col">
                    <h3 className="text-primary mb-2 text-xl font-semibold">
                        {R.AdultsInHousehold}
                    </h3>

                    <div className="text-(--color-text-secondary)">
                        {R.CannotInviteAdultsInHousehold}
                        <Link
                            to={`/${ProfilePaths.Access}`}
                            className="text-(--color-text-link)"
                        >
                            {R.Access}.
                        </Link>
                    </div>
                </div>

                <div className="flex h-full items-center">
                    <Button
                        variant="secondary"
                        outline
                        disabled
                        className="min-w-[195px]"
                    >
                        <MailIcon />
                        {R.InviteAdult}
                    </Button>
                </div>
            </div>
        </div>
    );
}
